import { useQueries } from '@tanstack/react-query';

import type {
  AdminEndorsementOpportunityFilters,
  ApiSuccess,
  EndorsementOpportunityFilters,
  EndorsementOpportunityRow,
} from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

import { createQueueListingHook } from './queue/createQueueListingHook';

type OpportunityPayload = { count: number; rows: EndorsementOpportunityRow[] };

export const useAdminEndorsementOpportunities = createQueueListingHook<
  EndorsementOpportunityRow,
  AdminEndorsementOpportunityFilters
>({
  baseQueryKey: queryKeys.policyEndorsements.admin(),
  queryKey: (filters) =>
    queryKeys.policyEndorsements.adminOpportunities(filters as Record<string, unknown> | undefined),
  fetchPage: async (filters) => {
    const response = await apiClient.get<ApiSuccess<OpportunityPayload>>(
      '/admin/policy-endorsements/opportunities',
      { params: filters },
    );
    return response.data.data;
  },
  getPageParams: (filters) => ({ page: filters?.page, limit: filters?.limit }),
  staleTime: 15_000,
});

export const useMyEndorsementOpportunities = createQueueListingHook<
  EndorsementOpportunityRow,
  EndorsementOpportunityFilters
>({
  baseQueryKey: queryKeys.policyEndorsements.mine(),
  queryKey: (filters) =>
    queryKeys.policyEndorsements.myOpportunities(filters as Record<string, unknown> | undefined),
  fetchPage: async (filters) => {
    const response = await apiClient.get<ApiSuccess<OpportunityPayload>>(
      '/policy-endorsements/opportunities/mine',
      { params: filters },
    );
    return response.data.data;
  },
  getPageParams: (filters) => ({ page: filters?.page, limit: filters?.limit }),
  staleTime: 15_000,
});

const COUNT_FILTERS = [
  { detectedVia: 'manual_lookup', outcome: 'pending' },
  { detectedVia: 'cpf_lookup', outcome: 'pending' },
  { detectedVia: 'plate_advisory', outcome: 'pending' },
  { detectedVia: 'plate_advisory', outcome: 'dismissed' },
] as const;

export function useEndorsementOpportunityCounts(
  scope: 'admin' | 'mine',
  options?: { enabled?: boolean },
) {
  const endpoint =
    scope === 'admin'
      ? '/admin/policy-endorsements/opportunities'
      : '/policy-endorsements/opportunities/mine';
  return useQueries({
    queries: COUNT_FILTERS.map((filters) => ({
      queryKey:
        scope === 'admin'
          ? queryKeys.policyEndorsements.adminOpportunities({ ...filters, page: 1, limit: 1 })
          : queryKeys.policyEndorsements.myOpportunities({ ...filters, page: 1, limit: 1 }),
      queryFn: async () => {
        const response = await apiClient.get<ApiSuccess<OpportunityPayload>>(endpoint, {
          params: { ...filters, page: 1, limit: 1 },
        });
        return response.data.data.count;
      },
      enabled: options?.enabled ?? true,
      staleTime: 15_000,
      refetchInterval: 30_000,
    })),
    combine: (results) => ({
      data: results.every((row) => row.data !== undefined)
        ? {
            inPool: (results[0]?.data ?? 0) + (results[1]?.data ?? 0),
            seenInQuotation: (results[2]?.data ?? 0) + (results[3]?.data ?? 0),
          }
        : undefined,
      isError: results.some((row) => row.isError),
      isFetching: results.some((row) => row.isFetching),
    }),
  });
}
